import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { seoArticles } from '@/mocks/seoArticles';
import { SITE_URL } from '@/constants';

export default function DynamicArticle() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 min-h-screen">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Articolo non trovato</h1>
            <p className="text-foreground-500 mb-8">L'articolo che stai cercando non è disponibile.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Torna alla Home
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </span>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1,
    description: article.seoDescription,
    author: {
      '@type': 'Organization',
      name: 'Itaobuy Spreadsheet Italia',
      url: SITE_URL,
    },
    datePublished: article.date,
    dateModified: article.date,
    url: `${SITE_URL}/articolo/${article.slug}`,
    inLanguage: 'it',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Guida Shopping', item: `${SITE_URL}/guida-shopping` },
      { '@type': 'ListItem', position: 3, name: article.h1, item: `${SITE_URL}/articolo/${article.slug}` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main className="pt-24">
        <article className="max-w-[900px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-foreground-600 transition-colors whitespace-nowrap">Home</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <Link to="/guida-shopping" className="hover:text-foreground-600 transition-colors whitespace-nowrap">Guida Shopping</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-600 font-medium line-clamp-1">{article.h1}</span>
          </nav>

          <header className="mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4 whitespace-nowrap">
              Articolo SEO · 2026
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-6">
              {article.h1}
            </h1>
            <p className="text-base text-foreground-500 leading-relaxed max-w-[750px]">
              {article.excerpt}
            </p>
          </header>

          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src={article.image}
              alt={article.h1}
              className="w-full h-auto"
              title={article.h1}
            />
          </div>

          {article.sections.map((section, idx) => (
            <section key={idx} className="mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                {idx + 1}. {section.heading}
              </h2>
              {section.paragraphs.map((p, pIdx) => (
                <p key={pIdx} className="text-foreground-600 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: p }} />
              ))}
            </section>
          ))}

          {article.faqs.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">
                Domande Frequenti
              </h2>
              <div className="space-y-3">
                {article.faqs.map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-xl border border-background-200/60 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground-400 group-open:rotate-45 transition-transform duration-200">
                        <i className="ri-add-line"></i>
                      </span>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </section>
          )}

          <section className="mb-12 bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-10">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
              Continua a Esplorare Itaobuy Spreadsheet
            </h2>
            <p className="text-foreground-600 leading-relaxed mb-6">
              Questo articolo fa parte della nostra collezione di guide SEO dedicate a <strong>Itaobuy Spreadsheet</strong>.
              Scopri altri articoli per approfondire ogni aspetto dello shopping online intelligente.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Torna alla Home
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line"></i>
                </span>
              </Link>
              <Link
                to="/guida-shopping"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-900 text-background-50 font-semibold text-sm hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                Tutte le Guide
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </span>
              </Link>
            </div>
          </section>

          <nav className="pt-8 border-t border-background-200/60">
            <p className="text-xs text-foreground-400 mb-3">Link utili:</p>
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap">
                Home — Itaobuy Spreadsheet
              </Link>
              {seoArticles.slice(0, 5).filter((a) => a.slug !== slug).map((a) => (
                <Link
                  key={a.slug}
                  to={`/articolo/${a.slug}`}
                  className="text-xs text-foreground-500 hover:text-foreground-700 bg-background-100 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
                >
                  {a.h1.length > 40 ? a.h1.slice(0, 40) + '...' : a.h1}
                </Link>
              ))}
            </div>
          </nav>
        </article>
      </main>
      <Footer />
    </>
  );
}